import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trophy, ClipboardList, CheckCircle2, TrendingUp } from "lucide-react";

const MEDAL_TONE = [
  "bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900",
  "bg-gradient-to-br from-slate-300 to-slate-400 text-slate-900",
  "bg-gradient-to-br from-orange-400 to-amber-600 text-white",
];

export default function RepLeaderboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/leaderboard").then(({ data }) => setData(data)); }, []);
  if (!data) return null;
  const rows = data.leaderboard || [];
  const maxScore = Math.max(...rows.map((r) => r.score), 1);
  return (
    <Card className="p-6 border-slate-200 shadow-none" data-testid="rep-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Weekly ranking</div>
          <h3 className="font-heading font-bold text-xl flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Rep leaderboard</h3>
          <p className="text-xs text-slate-500 mt-1 font-mono-plex">Since {data.since} · Score = 2×visits + 1×follow-ups done + 5×conversions</p>
        </div>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-400">No activity yet this week.</div>}
        {rows.map((r, i) => {
          const pct = (r.score / maxScore) * 100;
          const initials = (r.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
          return (
            <div key={r.user_id} className="grid grid-cols-12 gap-3 items-center py-2 border-b border-slate-100 last:border-b-0 row-enter" data-testid={`lb-row-${r.user_id}`}>
              <div className="col-span-1 flex justify-center">
                {i < 3 ? (
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${MEDAL_TONE[i]}`}>{i + 1}</div>
                ) : (
                  <div className="text-xs font-mono-plex text-slate-400">#{i + 1}</div>
                )}
              </div>
              <div className="col-span-4 flex items-center gap-2 min-w-0">
                <Avatar className="w-7 h-7">
                  {r.picture ? <AvatarImage src={r.picture} /> : null}
                  <AvatarFallback className="bg-slate-200 text-[10px]">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">{r.role}</div>
                </div>
              </div>
              <div className="col-span-1 text-center" title="Visits">
                <div className="text-sm font-mono-plex text-slate-900">{r.visits}</div>
                <div className="text-[9px] uppercase tracking-widest text-slate-400 font-mono-plex flex items-center justify-center gap-1"><ClipboardList className="w-3 h-3" /> visits</div>
              </div>
              <div className="col-span-2 text-center" title="Follow-ups completed">
                <div className="text-sm font-mono-plex text-slate-900">{r.followups_done}</div>
                <div className="text-[9px] uppercase tracking-widest text-slate-400 font-mono-plex flex items-center justify-center gap-1"><CheckCircle2 className="w-3 h-3" /> follow-ups</div>
              </div>
              <div className="col-span-1 text-center" title="Conversions to Active">
                <div className="text-sm font-mono-plex text-emerald-700">{r.conversions}</div>
                <div className="text-[9px] uppercase tracking-widest text-slate-400 font-mono-plex flex items-center justify-center gap-1"><TrendingUp className="w-3 h-3" /> conv.</div>
              </div>
              <div className="col-span-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-slate-900 to-emerald-600 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <Badge className="bg-slate-900 hover:bg-slate-900 shrink-0 font-mono-plex">{r.score}</Badge>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
